
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/components/AuthProvider";
import { toast } from "sonner";
import { StudentEvent } from "../types";
import { useQueryClient } from "@tanstack/react-query";
import { useLanguage } from "@/components/LanguageContext";
import { 
  fetchStudentByAuthId, 
  fetchTrainerSettings, 
  fetchScheduledEvents, 
  fetchScheduleRequests,
  setupRealtimeChannel
} from "../api/scheduleApi";
import {
  processScheduledEvents,
  processScheduleRequests,
  sortEventsByDate
} from "../utils/eventProcessors";
import { supabase } from "@/integrations/supabase/client";

export const useScheduleQueries = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [student, setStudent] = useState<any>(null);
  const [settings, setSettings] = useState<any>(null);
  const [trainerEvents, setTrainerEvents] = useState<StudentEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();

  // Fetch student data
  useEffect(() => {
    const getStudentData = async () => {
      if (!user?.id) return;
      console.log('=== FETCHING STUDENT DATA ===');
      console.log('Auth user ID:', user.id);
      const studentData = await fetchStudentByAuthId(user.id);
      console.log('Student data:', studentData);
      setStudent(studentData);
    };

    getStudentData();
  }, [user]);

  // Fetch trainer settings
  useEffect(() => {
    const getTrainerSettings = async () => {
      if (!student?.trainer_id) return;
      console.log('=== FETCHING TRAINER SETTINGS ===');
      console.log('Trainer ID:', student.trainer_id);
      const settingsData = await fetchTrainerSettings(student.trainer_id);
      console.log('Settings data:', settingsData);
      setSettings(settingsData);
    };

    if (student) {
      getTrainerSettings();
    }
  }, [student]);

  // Fetch events
  const fetchEvents = useCallback(async () => {
    if (!student?.trainer_id || !student?.id) return;
    setIsLoading(true);

    try {
      console.log('=== FETCHING EVENTS ===');
      console.log('Student ID:', student.id);
      console.log('Trainer ID:', student.trainer_id);
      
      // 1. Fetch ALL scheduled events from the trainer
      const scheduledEvents = await fetchScheduledEvents(student.trainer_id);
      console.log('Raw scheduled events:', scheduledEvents);
      
      // 2. Fetch ALL schedule requests from the trainer
      const scheduleRequests = await fetchScheduleRequests(student.trainer_id);
      console.log('Raw schedule requests:', scheduleRequests);
      
      // Process events
      // 1. Process all scheduled events
      const processedEvents = processScheduledEvents(scheduledEvents, student.id, language);
      console.log('Processed scheduled events:', processedEvents);
      
      // 2. Process ALL requests (including pending ones from other students)
      const requestEvents = processScheduleRequests(
        scheduleRequests, 
        student.id, 
        language,
        processedEvents
      );
      console.log('Processed request events:', requestEvents);
      
      // Combine and sort all events
      const allEvents = [...processedEvents, ...requestEvents];
      console.log('Combined events before sorting:', allEvents);
      
      const sortedEvents = sortEventsByDate(allEvents);
      console.log('Final sorted events:', sortedEvents);

      setTrainerEvents(sortedEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setIsLoading(false);
    }
  }, [student, language]);

  // Set up realtime listeners and fetch initial events
  useEffect(() => {
    if (student?.trainer_id && student?.id) {
      console.log('=== SETTING UP REALTIME AND INITIAL FETCH ===');
      fetchEvents();

      // Set up real-time listeners for updates
      const channel = setupRealtimeChannel(fetchEvents);

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [student, fetchEvents]);

  const removeEventLocally = (eventId: string) => {
    console.log('Removing event locally:', eventId);
    setTrainerEvents(prev => prev.filter(event => event.id !== eventId));
  };
  
  const updateEventLocally = (updatedEvent: StudentEvent) => {
    console.log('Updating event locally:', updatedEvent);
    setTrainerEvents(prev => 
      prev.map(event => event.id === updatedEvent.id ? updatedEvent : event)
    );
  };

  return {
    student,
    settings,
    trainerEvents,
    isLoading,
    removeEventLocally,
    updateEventLocally
  };
};
